// src/pages/SquareTraining.js
// Square-naming trainer: a square name (like "f6") appears translucent over
// the board — click that square. Right = green flash, wrong = red flash.
// Timed rounds (10s / 30s / 60s); the score is saved to `trainingResults`
// so coaches can review each student's history.
import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";
import { useAuth } from "../AuthContext";
import "./SquareTraining.css";

const FILES = ["a", "b", "c", "d", "e", "f", "g", "h"];

function randomSquare() {
  return FILES[Math.floor(Math.random() * 8)] + (1 + Math.floor(Math.random() * 8));
}

export default function SquareTraining() {
  const { user, userData } = useAuth();
  const [phase, setPhase] = useState("setup");        // setup | playing | done
  const [duration, setDuration] = useState(30);        // 10 | 30 | 60
  const [timeLeft, setTimeLeft] = useState(30);
  const [target, setTarget] = useState(randomSquare());
  const [score, setScore] = useState({ correct: 0, wrong: 0 });
  const [flash, setFlash] = useState(null);            // "green" | "red" | null
  const [saveState, setSaveState] = useState("");      // "" | saving | saved | failed
  const timerRef = useRef(null);
  const flashRef = useRef(null);
  const scoreRef = useRef({ correct: 0, wrong: 0 });

  // countdown
  useEffect(() => {
    if (phase !== "playing") return;
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); endRound(); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => () => { clearInterval(timerRef.current); clearTimeout(flashRef.current); }, []);

  function start() {
    scoreRef.current = { correct: 0, wrong: 0 };
    setScore({ correct: 0, wrong: 0 });
    setTimeLeft(duration);
    setTarget(randomSquare());
    setSaveState("");
    setPhase("playing");
  }

  function endRound() {
    setPhase("done");
    saveResult(scoreRef.current);
  }

  async function saveResult(finalScore) {
    if (!user) return;
    const attempted = finalScore.correct + finalScore.wrong;
    if (attempted === 0) { setSaveState(""); return; }   // nothing to record
    setSaveState("saving");
    try {
      await addDoc(collection(db, "trainingResults"), {
        studentId: user.uid,
        studentName: userData?.name || "",
        type: "square",
        duration,
        attempted,
        correct: finalScore.correct,
        wrong: finalScore.wrong,
        createdAt: new Date().toISOString(),
      });
      setSaveState("saved");
    } catch (err) {
      console.error("Could not save square-training result:", err);
      setSaveState("failed");
    }
  }

  function clickSquare(sq) {
    if (phase !== "playing") return;
    const right = sq === target;
    scoreRef.current = {
      correct: scoreRef.current.correct + (right ? 1 : 0),
      wrong: scoreRef.current.wrong + (right ? 0 : 1),
    };
    setScore({ ...scoreRef.current });
    setFlash(right ? "green" : "red");
    clearTimeout(flashRef.current);
    flashRef.current = setTimeout(() => setFlash(null), 220);
    if (right) {
      let next = randomSquare();
      while (next === sq) next = randomSquare();
      setTarget(next);
    }
  }

  const attempted = score.correct + score.wrong;
  const accuracy = attempted ? Math.round((score.correct / attempted) * 100) : 0;

  return (
    <div className="sq-training">
      {flash && <div className={`sq-flash ${flash}`} aria-hidden="true" />}

      <div className="sq-top">
        <h2 className="student-section-title" style={{ padding: 0, margin: 0 }}>🎯 Square Training</h2>
        {phase === "playing" && (
          <div className="sq-hud">
            <span className={`sq-timer ${timeLeft <= 5 ? "low" : ""}`}>⏱ {timeLeft}s</span>
            <span className="sq-live-score">✔ {score.correct} · ✘ {score.wrong}</span>
          </div>
        )}
      </div>

      {phase === "setup" && (
        <div className="sq-setup">
          <p className="sq-hint">A square name will appear on the board — click that square as fast as you can!</p>
          <div className="sq-durations">
            {[10, 30, 60].map(d => (
              <button key={d} className={`sq-dur-btn ${duration === d ? "active" : ""}`} onClick={() => setDuration(d)}>
                {d}s
              </button>
            ))}
          </div>
          <button className="btn btn-primary sq-start-btn" onClick={start}>Start ▶</button>
        </div>
      )}

      {phase === "done" && (
        <div className="sq-results">
          <div className="sq-results-emoji">{accuracy >= 80 ? "🏆" : accuracy >= 50 ? "👏" : "💪"}</div>
          <h3>Time's up!</h3>
          <div className="sq-results-nums">
            <div><span className="sq-num">{attempted}</span><span className="sq-num-label">attempted</span></div>
            <div><span className="sq-num good">{score.correct}</span><span className="sq-num-label">correct</span></div>
            <div><span className="sq-num bad">{score.wrong}</span><span className="sq-num-label">wrong</span></div>
            <div><span className="sq-num">{accuracy}%</span><span className="sq-num-label">accuracy</span></div>
          </div>
          <p className="sq-save-state">
            {saveState === "saving" && "Saving your score…"}
            {saveState === "saved" && "✓ Score recorded"}
            {saveState === "failed" && "⚠ Couldn't save — check your connection"}
          </p>
          <button className="btn btn-primary" onClick={() => setPhase("setup")}>Play Again</button>
        </div>
      )}

      {(phase === "playing" || phase === "setup") && (
        <div className={`sq-board-wrap ${phase === "setup" ? "dimmed" : ""}`}>
          <div className="sq-board">
            {Array.from({ length: 64 }, (_, i) => {
              const file = i % 8, rank = 8 - Math.floor(i / 8);
              const name = FILES[file] + rank;
              const dark = (Math.floor(i / 8) + file) % 2 === 1;
              return (
                <div key={name}
                  className={`sq-cell ${dark ? "dark" : "light"}`}
                  onClick={() => clickSquare(name)}>
                  {file === 0 && <span className="sq-coord rank">{rank}</span>}
                  {rank === 1 && <span className="sq-coord file">{FILES[file]}</span>}
                </div>
              );
            })}
            {phase === "playing" && <div className="sq-target" aria-live="polite">{target}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
